import { ReactNode } from 'react';
import { BaseInputWrapper } from './base-input-wrapper';
import styles from './text-area.module.css';
import { ErrorField } from '../error-field';
import { FieldError } from 'react-hook-form';
import clsx from 'clsx';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label?: ReactNode;
  subLabel?: ReactNode;
  description?: string;
}

export function TextArea({
  name,
  label,
  subLabel,
  className,
  description,
  id,
  maxLength,
  ...restProps
}: TextAreaProps) {
  return (
    <BaseInputWrapper<HTMLTextAreaElement>
      id={id}
      name={name}
      label={label}
      subLabel={subLabel}
      className={className}
      restProps={restProps}
      withIconsIndicators={false}
      withErrorMessage={false}
    >
      {({ fieldProps, hasError, error, inputClassName, getValue, htmlFor }) => (
        <div className={styles.textAreaContainer}>
          <textarea id={htmlFor} className={inputClassName} maxLength={maxLength} {...fieldProps} />
          <TextAreaFooter
            hasError={hasError}
            error={error}
            description={description}
            maxLength={maxLength}
            valueLength={getValue()?.length ?? 0}
          />
        </div>
      )}
    </BaseInputWrapper>
  );
}

interface TextAreaFooterProps {
  hasError: boolean;
  error: FieldError | undefined;
  description: string | undefined;
  maxLength: number | undefined;
  valueLength: number;
}

function TextAreaFooter({
  hasError,
  error,
  description,
  maxLength,
  valueLength,
}: TextAreaFooterProps) {
  const showDescription = !hasError && !!description;
  const showCounter = maxLength !== undefined;

  if (!hasError && !showDescription && !showCounter) {
    return null;
  }

  return (
    <div className={styles.textAreaRow}>
      {hasError && <ErrorField error={error} />}
      {showDescription && (
        <span className={clsx('caption', styles.description)}>{description}</span>
      )}
      {showCounter && (
        <span className={styles.maxLength}>
          {valueLength}/{maxLength}
        </span>
      )}
    </div>
  );
}
